/**
 * Prototype deck definition (108 cards) and card types.
 * Each card has a unique, stable id. The deck actually used in a game
 * is filtered by the rules configuration (max number, presence of 0s
 * and Jokers): see buildDeckFor().
 */

export type CardColor = 'red' | 'blue' | 'green' | 'yellow'
export const CARD_COLORS: readonly CardColor[] = ['red', 'blue', 'green', 'yellow']

export type CardKind = 'number' | 'zero' | 'skip' | 'reverse' | 'plus2' | 'wild' | 'wild4'

export interface Card {
  id: string
  kind: CardKind
  /** Card color; absent for Joker and +4 */
  color?: CardColor
  /** Value 1..9 for number cards */
  value?: number
}

/** The full reference deck (108 cards), in canonical order. */
export function buildDeck(): Card[] {
  const cards: Card[] = []
  for (const color of CARD_COLORS) {
    cards.push({ id: `${color}-0`, kind: 'zero', color })
    for (let value = 1; value <= 9; value++) {
      cards.push({ id: `${color}-${value}-a`, kind: 'number', color, value })
      cards.push({ id: `${color}-${value}-b`, kind: 'number', color, value })
    }
    for (const suffix of ['a', 'b'] as const) {
      cards.push({ id: `${color}-skip-${suffix}`, kind: 'skip', color })
      cards.push({ id: `${color}-reverse-${suffix}`, kind: 'reverse', color })
      cards.push({ id: `${color}-plus2-${suffix}`, kind: 'plus2', color })
    }
  }
  for (const suffix of ['a', 'b', 'c', 'd'] as const) {
    cards.push({ id: `wild-${suffix}`, kind: 'wild' })
  }
  for (const suffix of ['a', 'b', 'c', 'd'] as const) {
    cards.push({ id: `wild4-${suffix}`, kind: 'wild4' })
  }
  return cards
}

export const DECK_SIZE = 108

export interface DeckOptions {
  /** Maximum number of the number cards kept (1..9). */
  maxCardNumber: number
  includeZero: boolean
  includeJokers: boolean
}

/** Deck filtered according to a game's rules. */
export function buildDeckFor(opts: DeckOptions): Card[] {
  return buildDeck().filter((c) => {
    if (c.kind === 'number') return (c.value ?? 0) <= opts.maxCardNumber
    if (c.kind === 'zero') return opts.includeZero
    if (c.kind === 'wild') return opts.includeJokers
    return true
  })
}

const DECK_INDEX: ReadonlyMap<string, Card> = new Map(buildDeck().map((c) => [c.id, c]))

export function cardById(id: string): Card {
  const card = DECK_INDEX.get(id)
  if (!card) throw new Error(`Carte inconnue : ${id}`)
  return card
}

export function isCardId(id: string): boolean {
  return DECK_INDEX.has(id)
}
